// pages/app/Voice.tsx — think-aloud recording (Key1 audio) + live voice (Key2) (spec §6.4–6.5).
import { useCallback, useEffect, useRef, useState } from 'react';
import { useServices } from '../../app/AppContext';
import { useSetupContext } from '../../app/SetupContext';
import { readFileAsBase64 } from '../../lib/files';
import { startRealtimeVoice, type RealtimeVoiceHandle } from '../../lib/realtimeVoice';
import { useCallGuard } from '../../lib/useCallGuard';
import { ConfirmDialog } from '../../components/Dialog';
import type { VoiceCall } from '../../lib/api';
import { PhoneIcon, StopIcon, VoiceIcon } from '../../components/icons';

type Turn = { role: 'user' | 'assistant'; text: string };
type CallState = 'idle' | 'connecting' | 'live' | 'ended';

function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

export function Voice() {
  const { api } = useServices();
  const { status } = useSetupContext();
  const [recording, setRecording] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);

  // Live voice (Key2)
  const [callState, setCallState] = useState<CallState>('idle');
  const [transcript, setTranscript] = useState<Turn[]>([]);
  const [liveError, setLiveError] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement>(null);
  const callRef = useRef<RealtimeVoiceHandle | null>(null);
  const callStartRef = useRef<number>(0);
  const [calls, setCalls] = useState<VoiceCall[]>([]);

  const canThinkAloud = Boolean(status?.voiceRecordingEnabled);
  const canLiveVoice = Boolean(status?.liveVoiceEnabled);

  const loadCalls = useCallback(async () => {
    try {
      const res = await api.listVoiceCalls();
      setCalls(res.calls);
    } catch {
      /* listing is best-effort */
    }
  }, [api]);

  const inCall = callState === 'connecting' || callState === 'live';
  function endCall() {
    const handle = callRef.current;
    callRef.current = null;
    handle?.stop();
    setCallState((s) => (s === 'connecting' || s === 'live' ? 'ended' : s));
    // Persist the transcript (text only, no audio) then refresh the list (spec §6.5).
    if (handle && transcript.length > 0) {
      void api
        .saveCallTranscript({
          transcript,
          startedAt: new Date(callStartRef.current || Date.now()).toISOString(),
        })
        .then(loadCalls)
        .catch(() => {});
    }
  }
  // A live call must be cancelled on any navigation or tab close (spec §6.5).
  const callGuard = useCallGuard(inCall, endCall);

  useEffect(() => {
    if (canLiveVoice) void loadCalls();
  }, [canLiveVoice, loadCalls]);

  if (!canThinkAloud && !canLiveVoice) {
    return (
      <div className="mx-auto max-w-5xl animate-fade-in">
        <div className="card flex flex-col items-center py-12 text-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-surface-3 text-fg-muted">
            <VoiceIcon />
          </div>
          <h2 className="mt-4 text-lg font-semibold">Voice is off</h2>
          <p className="mt-1 max-w-sm text-sm text-fg-muted">
            Think-aloud requires an audio-multimodal Key1 model. For a live conversation, add a Key2
            voice provider (Azure AI Foundry) in Settings.
          </p>
        </div>
      </div>
    );
  }

  async function upload(blob: Blob, stream: MediaStream) {
    setBusy(true);
    try {
      const audioBase64 = await readFileAsBase64(blob);
      const { sessionId } = await api.startVoiceSession();
      await api.voiceTurn({ sessionId, mimeType: blob.type || 'audio/webm', audioBase64 });
      setMessage('Recording sent — processing your think-aloud.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setBusy(false);
      stream.getTracks().forEach((t) => t.stop());
    }
  }

  async function start() {
    setError(null);
    setMessage(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      chunksRef.current = [];
      rec.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      rec.onstop = () => void upload(new Blob(chunksRef.current, { type: 'audio/webm' }), stream);
      rec.start();
      recorderRef.current = rec;
      setRecording(true);
    } catch {
      setError('Microphone access failed.');
    }
  }

  function stop() {
    recorderRef.current?.stop();
    setRecording(false);
  }

  async function startCall() {
    setLiveError(null);
    setTranscript([]);
    callStartRef.current = Date.now();
    setCallState('connecting');
    try {
      callRef.current = await startRealtimeVoice({
        startSession: () => api.startRealtimeSession(),
        createPeerConnection: () => new RTCPeerConnection(),
        getUserMedia: (c) => navigator.mediaDevices.getUserMedia(c),
        fetchFn: fetch.bind(globalThis),
        attachRemoteAudio: (s) => {
          if (audioRef.current) audioRef.current.srcObject = s;
        },
        onTranscript: (role, text) => setTranscript((t) => [...t, { role, text }]),
        onToolCall: (name, args) => runVoiceTool(name, args),
        onState: (s) => {
          if (s === 'failed' || s === 'disconnected' || s === 'closed') setCallState('ended');
        },
      });
      setCallState('live');
    } catch (err) {
      setLiveError(err instanceof Error ? err.message : 'Could not start live voice');
      setCallState('idle');
    }
  }

  // Tools the live twin can call mid-conversation to read/write the owner's memory (spec §6.5).
  async function runVoiceTool(name: string, args: Record<string, unknown>): Promise<string> {
    try {
      if (name === 'recall_memory') {
        const query = typeof args.query === 'string' ? args.query : '';
        const { hits } = await api.recall({ query });
        if (hits.length === 0) return 'No memory found about that.';
        return hits
          .slice(0, 6)
          .map((h) => `- ${h.content_distilled}`)
          .join('\n');
      }
      if (name === 'remember_fact') {
        const content = typeof args.content === 'string' ? args.content : '';
        if (!content.trim()) return 'Nothing to remember.';
        await api.rememberFact({ content });
        return 'Saved to memory.';
      }
      if (name === 'web_search') {
        const query = typeof args.query === 'string' ? args.query : '';
        const { results } = await api.toolSearch({ query });
        if (results.length === 0) return 'No results found.';
        return results
          .slice(0, 5)
          .map((r) => `- ${r.title} (${r.url})`)
          .join('\n');
      }
      if (name === 'fetch_url') {
        const url = typeof args.url === 'string' ? args.url : '';
        if (!url.trim()) return 'No URL provided.';
        const { text } = await api.toolFetch({ url });
        return text.slice(0, 4000) || 'That page had no readable text.';
      }
      return `Unknown tool: ${name}`;
    } catch {
      return 'That memory action failed.';
    }
  }

  return (
    <div className="mx-auto max-w-5xl animate-fade-in space-y-6">
      <ConfirmDialog
        open={callGuard.pending !== null}
        title="End the live call?"
        message="A live call is in progress. Leave this page and end the call, or stay on the call?"
        confirmLabel="Leave & end call"
        cancelLabel="Stay on call"
        danger
        onConfirm={callGuard.confirm}
        onCancel={callGuard.cancel}
      />
      {canLiveVoice ? (
        <section className="card space-y-4">
          <div>
            <h2 className="text-base font-semibold">Live voice — talk to your twin</h2>
            <p className="mt-1 text-sm text-fg-muted">
              A realtime conversation with your working self, powered by your Azure AI Foundry voice
              model.
            </p>
          </div>
          <audio ref={audioRef} autoPlay className="hidden" />
          <div className="flex flex-col items-center gap-4 py-4">
            {inCall ? (
              <button
                type="button"
                onClick={endCall}
                aria-label="End call"
                className="relative flex h-20 w-20 items-center justify-center rounded-full bg-danger/10 text-danger ring-4 ring-danger/20 transition hover:bg-danger/20"
              >
                {callState === 'connecting' ? (
                  <span className="absolute inset-0 animate-ping rounded-full bg-danger/20" />
                ) : null}
                <PhoneIcon className="h-8 w-8" />
              </button>
            ) : (
              <button
                type="button"
                onClick={() => void startCall()}
                aria-label="Start call"
                className="flex h-20 w-20 items-center justify-center rounded-full bg-brand text-brand-fg shadow-brand-glow transition hover:brightness-110"
              >
                <PhoneIcon className="h-8 w-8" />
              </button>
            )}
            <span className="text-sm font-medium text-fg-muted">
              {callState === 'connecting'
                ? 'Connecting…'
                : callState === 'live'
                  ? 'Live — start speaking'
                  : callState === 'ended'
                    ? 'Call ended'
                    : 'Start a live call'}
            </span>
          </div>
          {liveError ? (
            <p
              className="rounded-xl border border-danger/20 bg-danger/10 px-3.5 py-2.5 text-sm text-danger"
              role="alert"
            >
              {liveError}
            </p>
          ) : null}
          {transcript.length > 0 ? (
            <div>
              <p className="mb-1.5 text-xs font-medium uppercase tracking-wide text-fg-subtle">
                Live captions
              </p>
              <ul className="space-y-2" data-testid="voice-transcript" aria-live="polite">
                {transcript.map((t, i) => (
                  <li key={i} className={t.role === 'user' ? 'text-right' : 'text-left'}>
                    <span
                      className={`inline-block rounded-2xl px-3 py-1.5 text-sm ${
                        t.role === 'user' ? 'bg-brand text-brand-fg' : 'bg-surface-2 text-fg'
                      }`}
                    >
                      {t.text}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </section>
      ) : null}

      {canLiveVoice && calls.length > 0 ? (
        <section className="card space-y-3" data-testid="past-calls">
          <h2 className="text-base font-semibold">Past calls</h2>
          <ul className="divide-y divide-border">
            {calls.map((c) => (
              <li key={c.id} className="py-3">
                <details>
                  <summary className="flex cursor-pointer items-center justify-between gap-3 text-sm">
                    <span className="font-medium">{new Date(c.createdAt).toLocaleString()}</span>
                    <span className="text-fg-muted">
                      {formatDuration(c.durationSeconds)} · {c.turnCount} turns
                    </span>
                  </summary>
                  <ul className="mt-2 space-y-1.5">
                    {c.transcript.map((t, i) => (
                      <li key={i} className="text-sm">
                        <span className="font-medium text-fg-subtle">
                          {t.role === 'user' ? 'You' : 'Twin'}:
                        </span>{' '}
                        {t.text}
                      </li>
                    ))}
                  </ul>
                </details>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {canThinkAloud ? (
        <section className="space-y-4">
          <div>
            <h2 className="text-base font-semibold">Think-aloud recording</h2>
            <p className="mt-1 text-sm text-fg-muted">
              Record yourself talking through how you approach a problem — processed directly by
              your Key1 model, no back-and-forth.
            </p>
          </div>

          <div className="card flex flex-col items-center gap-5 py-10">
            {recording ? (
              <button
                type="button"
                onClick={stop}
                aria-label="Stop recording"
                className="relative flex h-20 w-20 items-center justify-center rounded-full bg-danger/10 text-danger ring-4 ring-danger/20 transition hover:bg-danger/20"
              >
                <span className="absolute inset-0 animate-ping rounded-full bg-danger/20" />
                <StopIcon className="h-7 w-7" />
              </button>
            ) : (
              <button
                type="button"
                onClick={() => void start()}
                disabled={busy}
                aria-label="Start recording"
                className="flex h-20 w-20 items-center justify-center rounded-full bg-brand text-brand-fg shadow-brand-glow transition hover:brightness-110 disabled:opacity-50"
              >
                <VoiceIcon className="h-8 w-8" />
              </button>
            )}
            <span className="text-sm font-medium text-fg-muted">
              {recording ? 'Recording… tap to stop' : busy ? 'Uploading…' : 'Tap to record'}
            </span>
          </div>

          {message ? (
            <p
              className="rounded-xl border border-success/20 bg-success/10 px-3.5 py-2.5 text-sm text-success"
              role="status"
            >
              {message}
            </p>
          ) : null}
          {error ? (
            <p
              className="rounded-xl border border-danger/20 bg-danger/10 px-3.5 py-2.5 text-sm text-danger"
              role="alert"
            >
              {error}
            </p>
          ) : null}
        </section>
      ) : null}
    </div>
  );
}
